import React, { useState } from 'react'

import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { addDays, isBefore, isToday, parseISO } from 'date-fns'
import moment from 'moment'
import { useDispatch } from 'react-redux'

import { modifyLogger } from '../../actions/loggerActions'
import { setDisplayedDate, setFocus } from '../../actions/trackerActions'
import { displayMessage } from '../../actions/utilActions'
import { Input, Focus, MessageType } from '../../consts'
import { useSelector } from '../../hooks/useSelector'

import { CustomTextField } from './styles'

const ARROWS = {
  ArrowUp: 1,
  ArrowDown: -1,
  j: 1,
  k: -1,
}

type Props = {
  value: string //ISO string

  handleSubmit: (focusLogger?: boolean, timestampOverride?: Date) => void
}

export const LoggerDateInput = ({ value, handleSubmit }: Props) => {
  const [opened, setOpened] = useState(false)

  const dispatch = useDispatch()
  const minDate = useSelector((state) => state.minimalDate)

  const setNewDate = (newDate: Date) => {
    if (isBefore(newDate, minDate)) {
      dispatch(
        displayMessage(
          ['Unable to show worklogs. Check "days to show" in settings.'],
          MessageType.ERROR
        )
      )
      return
    }
    const newDateISO = newDate.toISOString()
    dispatch(modifyLogger(Input.TIMESTAMP, newDateISO))
    dispatch(setDisplayedDate(newDateISO))
  }

  const handleArrow = (key: string) => {
    if (ARROWS[key] > 0 && isToday(parseISO(value))) return
    const newDate = addDays(parseISO(value), ARROWS[key])
    setNewDate(newDate)
  }

  const handleChange = (date: moment.Moment | null) => {
    if (!date) return
    setNewDate(date.toDate())
  }

  const handleTab = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    dispatch(setFocus({ focusType: Focus.DESCRIPTION }))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const keyBindings = {
      Enter: () => handleSubmit(e.ctrlKey),
      Tab: () => handleTab(e),
      ArrowUp: () => handleArrow(e.key),
      ArrowDown: () => handleArrow(e.key),
      j: () => handleArrow(e.key),
      k: () => handleArrow(e.key),
    }
    !!keyBindings[e.key] && keyBindings[e.key]()
  }

  return (
    <div data-cy="logger-date">
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <DatePicker
          renderInput={(props) => (
            <CustomTextField
              {...props}
              inputProps={{
                ...props.inputProps,
                readOnly: true,
                // for some unknown reason value is not changing when moving with keyboard arrows
                // (workaround, remove if possible)
                value: moment(value).format('MMM DD, ddd'),
              }}
              InputProps={{ ...props.InputProps, endAdornment: undefined }}
              variant="outlined"
              label="Date"
              onClick={() => setOpened(true)}
              onKeyDown={handleKeyDown}
            />
          )}
          onChange={handleChange}
          value={value}
          open={opened}
          onOpen={() => setOpened(true)}
          onClose={() => setOpened(false)}
          disableFuture
          inputFormat="MMM DD, ddd"
          disableMaskedInput
        />
      </LocalizationProvider>
    </div>
  )
}
